import {
  CardsIcon,
  CarIcon,
  Icon,
  ListChecksIcon,
  MapPinIcon,
  MotorcycleIcon,
  UserSoundIcon,
} from '@phosphor-icons/react'
import { ReactElement, ReactNode } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import IconGradientWrapper from '@/src/components/core/icons/IconGradientWrapper'
import Container from '@/src/components/layout/Container'

function formatLocations(
  locations: FahrlehrerCh.TeacherProfileLocation[],
  max: number,
  selectedLocation: FahrlehrerCh.TeacherProfileLocation
): string {
  const rest = locations.filter((l) => l.id !== selectedLocation.id)
  const sorted = [
    selectedLocation,
    ...rest.filter((l) => l.hasBlinkLandingpage),
    ...rest.filter((l) => !l.hasBlinkLandingpage),
  ]
  const names = sorted.map((l) => l.name)
  if (names.length <= max) return names.join(', ')
  return `${names.slice(0, max).join(', ')} +${names.length - max}`
}

function formatCategories(categories: string[]): string {
  let categoryString = ''
  if (categories.some((c) => c.startsWith('B'))) {
    categoryString += 'Auto (Kat. B)'
  }
  if (categories.some((c) => c.startsWith('A'))) {
    categoryString += (categoryString ? ' & ' : '') + 'Motorrad (Kat. A)'
  }
  return categoryString
}

const transmissionLabels: Record<string, string> = {
  automatic: 'Automat',
  manual: 'Geschaltet',
}

function formatVehicles(
  vehicles: FahrlehrerCh.TeacherProfileVehicle[]
): string {
  const grouped = vehicles
    .filter((c) => c.brand && c.model)
    .reduce<Record<string, Set<string>>>((acc, c) => {
      const brand = c.brand!
      const model = c.model!
      const fullModel = `${brand} ${model}`
      if (!acc[fullModel]) acc[fullModel] = new Set()
      const transmission = c.transmissionType
        ? (transmissionLabels[c.transmissionType] ?? c.transmissionType)
        : null
      if (transmission) acc[fullModel].add(transmission)
      return acc
    }, {})

  return Object.entries(grouped)
    .map(([fullModel, transmissions]) => {
      const parts = [...transmissions]
      if (parts.length === 0) return fullModel
      return `${fullModel}, ${parts.join(' & ')}`
    })
    .join(' & ')
}

const InfoCard = ({
  value,
  label,
  icon,
}: {
  value: ReactNode
  label: string
  icon: ReactElement<Icon>
}) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
    <div className="flex-1">
      <p className="font-black md:text-lg">{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
    <div className="shrink-0">
      <IconGradientWrapper from="#1a34d0" to="#1628a4">
        {icon}
      </IconGradientWrapper>
    </div>
  </div>
)

const Info = ({
  certificationSince,
  lessonLanguages,
  locations,
  teacherCategories,
  vehicles,
  selectedLocation,
}: FahrlehrerCh.TeacherProfile & {
  selectedLocation: FahrlehrerCh.TeacherProfileLocation
}) => {
  const yearsExperience = certificationSince
    ? new Date().getFullYear() - certificationSince
    : null

  const autoCars = vehicles.filter((c) => c.type === 'car')
  const motorcycleCars = vehicles.filter((c) => c.type === 'motorcycle')

  return (
    <div className="mt-12">
      <section id="profile">
        <Container>
          <h2 className="text-2xl font-black md:text-7xl">Steckbrief</h2>
          <div className="mt-6 grid gap-3 md:grid-cols-2 md:gap-5">
            {yearsExperience !== null && (
              <InfoCard
                value={yearsExperience}
                label="Jahre Erfahrung"
                icon={<ListChecksIcon size={32} weight="bold" />}
              />
            )}
            {lessonLanguages.length > 0 && (
              <InfoCard
                value={lessonLanguages.join(', ')}
                label="Unterrichtssprache"
                icon={<UserSoundIcon size={32} weight="bold" />}
              />
            )}
            {locations.length > 0 && (
              <InfoCard
                value={
                  <>
                    <span className="lg:hidden">
                      {formatLocations(locations, 2, selectedLocation)}
                    </span>
                    <span className="hidden lg:inline">
                      {formatLocations(locations, 4, selectedLocation)}
                    </span>
                  </>
                }
                label="Standorte"
                icon={<MapPinIcon size={32} weight="bold" />}
              />
            )}
            <InfoCard
              value={formatCategories(teacherCategories)}
              label="Kategorien"
              icon={<CardsIcon size={32} weight="bold" />}
            />
            {autoCars.length > 0 && (
              <InfoCard
                value={formatVehicles(autoCars)}
                label="Auto"
                icon={<CarIcon size={32} weight="bold" />}
              />
            )}
            {motorcycleCars.length > 0 && (
              <InfoCard
                value={formatVehicles(motorcycleCars)}
                label="Motorrad"
                icon={<MotorcycleIcon size={32} weight="bold" />}
              />
            )}
          </div>
        </Container>
      </section>
    </div>
  )
}

export default Info
